/**
 * Slice Redux pour l'authentification
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '../../services/api.service';
import { Utilisateur, LoginCredentials } from '../../types';

interface AuthState {
  user: Utilisateur | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

// Récupérer l'utilisateur sauvegardé depuis localStorage
const getSavedUser = (): Utilisateur | null => {
  try {
    const saved = localStorage.getItem('user_data');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const initialState: AuthState = {
  user: getSavedUser(),
  token: localStorage.getItem('auth_token') || localStorage.getItem('access_token'),
  isAuthenticated: !!(localStorage.getItem('auth_token') || localStorage.getItem('access_token')),
  loading: false,
  error: null,
};

// Thunks asynchrones
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const tokens = await authService.login(credentials);
      localStorage.setItem('auth_token', tokens.access);
      if (tokens.refresh) {
        localStorage.setItem('refresh_token', tokens.refresh);
      }

      // Récupérer les informations de l'utilisateur
      const user = await authService.getCurrentUser();
      localStorage.setItem('user_data', JSON.stringify(user));

      return { user, token: tokens.access };
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (detail) return rejectWithValue(detail);
      if (error.code === 'ERR_NETWORK' || !error.response) {
        return rejectWithValue('Impossible de contacter le serveur. Vérifiez votre connexion internet.');
      }
      return rejectWithValue('Erreur de connexion. Veuillez réessayer.');
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();
      return user;
    } catch (error: any) {
      // 401/403 = token invalide → déconnecter
      // Erreur réseau = ne pas déconnecter
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        return rejectWithValue('session_expired');
      }
      return rejectWithValue('network_error');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      localStorage.removeItem('auth:session_locked');
      localStorage.removeItem('auth:session_locked_at');
      // Fix 2026-08-27 : propage le logout à tous les onglets du même
      // navigateur via BroadcastChannel + storage event (fallback).
      // Import dynamique pour éviter cycle store↔components.
      try {
        import('../../components/layout/InactivityLock').then(m => {
          try { m.broadcastLogoutToAllTabs?.(); } catch {}
        }).catch(() => {});
      } catch {}
    },
    clearError: (state) => {
      state.error = null;
    },
    // Patch local d'un champ user — utilisé après upload de photo, etc.
    // Évite un round-trip /utilisateurs/me/ pour des modifs ciblées.
    patchUser: (state, action: PayloadAction<Partial<Utilisateur>>) => {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
      try {
        localStorage.setItem('user_data', JSON.stringify(state.user));
      } catch {
        // localStorage plein ou indisponible : on continue silencieusement
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get Current User
    builder
      .addCase(getCurrentUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem('user_data', JSON.stringify(action.payload));
      })
      .addCase(getCurrentUser.rejected, (state, action) => {
        state.loading = false;
        // Seulement déconnecter si le token est invalide (401/403)
        // Pas si c'est une erreur réseau temporaire
        if (action.payload === 'session_expired') {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          localStorage.removeItem('auth_token');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_data');
        }
      });
  },
});

export const { logout, clearError, patchUser } = authSlice.actions;
export default authSlice.reducer;
